import { useEffect, useState } from "react";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/Button";
import { IdCapturePanel } from "../components/IdCapturePanel";
import { VideoCapturePanel } from "../components/VideoCapturePanel";
import { VIDEO_MAX_SECONDS } from "../media/limits";
import { mediaIsDurable } from "../media/uploadService";
import { onboardingStore } from "../auth/onboardingStore";
import { useT } from "../i18n";
import { closeVerify, openVerify, readVerifyStatus } from "./verifyGate";
import { saveVerifyMedia } from "./verifyMedia";

export function VerifyHost({
  pane,
  userId,
}: {
  pane: "id" | "video";
  userId: string;
}) {
  const t = useT();
  const [tick, setTick] = useState(0);
  const row = onboardingStore.load(userId);
  const id = row?.answers.governmentId ?? null;
  const video = row?.answers.confirmationVideo ?? null;
  const status = readVerifyStatus(userId);
  const showing: "id" | "video" =
    pane === "video" && (status.needsVideo || !status.needsId) ? "video" : "id";

  useEffect(() => {
    if (pane === "id" && !status.needsId && status.needsVideo) {
      window.location.hash = "#/match/verify/video";
    } else if (pane === "video" && !status.needsVideo && status.needsId) {
      window.location.hash = "#/match/verify/id";
    }
  }, [pane, status.needsId, status.needsVideo]);

  const idReady = mediaIsDurable(id);
  const videoReady = mediaIsDurable(video);
  const canContinue = showing === "id" ? idReady : videoReady;

  const continueNext = () => {
    const next = readVerifyStatus(userId);
    if (next.state === "unverified") {
      if (showing === "id" && next.needsVideo) {
        window.location.hash = "#/match/verify/video";
        return;
      }
      if (showing === "video" && next.needsId) {
        window.location.hash = "#/match/verify/id";
        return;
      }
    }
    closeVerify();
  };

  return (
    <>
      <header className="match-top">
        <div className="match-brand">
          <BrandMark compact />
          <span className="match-brand__tag">{t("match.verify.brandTag")}</span>
        </div>
        <nav className="match-nav">
          <button type="button" className="match-nav__link" onClick={() => closeVerify()}>
            {t("match.verify.back")}
          </button>
        </nav>
      </header>
      <main className="match-main">
        <div className="match-list screen-enter match-verify" key={`${showing}-${tick}`}>
          {showing === "id" ? (
            <>
              <header className="screen-head">
                <div className="screen-head__eyebrow">{t("match.verify.idEyebrow")}</div>
                <h1 className="screen-head__title">{t("verify.id.title")}</h1>
                <p className="screen-head__text">{t("match.verify.idText")}</p>
              </header>
              <IdCapturePanel
                asset={id}
                userId={userId}
                onChange={(asset) => {
                  saveVerifyMedia(userId, { governmentId: asset });
                  setTick((n) => n + 1);
                }}
              />
            </>
          ) : (
            <>
              <header className="screen-head">
                <div className="screen-head__eyebrow">{t("match.verify.videoEyebrow")}</div>
                <h1 className="screen-head__title">{t("verify.video.title")}</h1>
                <p className="screen-head__text">
                  {t("match.verify.videoText", { seconds: VIDEO_MAX_SECONDS })}
                </p>
              </header>
              <VideoCapturePanel
                asset={video}
                userId={userId}
                onChange={(asset) => {
                  saveVerifyMedia(userId, { confirmationVideo: asset });
                  setTick((n) => n + 1);
                }}
              />
            </>
          )}
          <div className="match-verify__footer">
            <Button onClick={continueNext} disabled={!canContinue}>
              {t("match.verify.continue")}
            </Button>
          </div>
        </div>
      </main>
    </>
  );
}

export function VerifyStatusCard({
  userId,
  compact,
}: {
  userId: string;
  compact?: boolean;
}) {
  const t = useT();
  const status = readVerifyStatus(userId);

  if (status.state === "verified") {
    return compact ? null : (
      <p className="match-list__sub">{t("match.verify.done")}</p>
    );
  }

  if (status.state === "submitted") {
    return <p className="match-list__sub">{t("match.verify.submitted")}</p>;
  }

  const cta = status.needsId
    ? t("match.verify.cta")
    : t("match.verify.videoCta");

  return (
    <div className="match-verify-card">
      <strong>{t("match.verify.title")}</strong>
      {compact ? null : <p>{t("match.verify.sub")}</p>}
      <button
        type="button"
        className="match-act match-act--like"
        onClick={() => {
          openVerify({
            needsId: status.needsId,
            needsVideo: status.needsVideo,
            returnHash: window.location.hash,
          });
        }}
      >
        {cta}
      </button>
    </div>
  );
}
